<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">Provincias</h1>
          <p class="text-sm text-gray-400 mt-1">Administrá las provincias disponibles.</p>
        </div>
        <button
          type="button"
          class="btn-bochas-rojo w-full text-center text-sm sm:w-auto"
          :disabled="loading || !!error || saving"
          @click="openForm()"
        >
          + Nueva provincia
        </button>
      </div>

      <div v-if="loading" class="text-center py-12" role="status" aria-label="Cargando provincias">
        <div class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"></div>
      </div>
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4" role="alert">
        <p>{{ error }}</p>
        <button type="button" class="mt-3 underline" @click="load">Reintentar</button>
      </div>
      <template v-else>
        <form v-if="formOpen" class="card-fbochas p-4 sm:p-6 mb-6 space-y-4" @submit.prevent="onSave">
          <h2 class="text-lg font-bold text-white">{{ editingId ? 'Editar provincia' : 'Nueva provincia' }}</h2>
          <div>
            <label for="provincia-name" class="block text-sm font-medium text-gray-300 mb-2">Nombre de la provincia</label>
            <input
              id="provincia-name"
              ref="nameInput"
              v-model="name"
              type="text"
              required
              maxlength="100"
              :disabled="saving"
              placeholder="Ej: Santa Fe"
              class="w-full sm:max-w-md px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
            />
          </div>
          <p v-if="formError" class="text-sm text-red-300" role="alert">{{ formError }}</p>
          <div class="flex justify-end gap-2">
            <button type="button" class="btn-bochas-trans text-sm" :disabled="saving" @click="formOpen = false">Cancelar</button>
            <button type="submit" class="btn-bochas-rojo text-sm" :disabled="saving">{{ saving ? 'Guardando…' : 'Guardar provincia' }}</button>
          </div>
        </form>

        <p v-if="success" class="text-sm text-green-300 mb-4" role="status">{{ success }}</p>
        <div v-if="provincias.length" class="mb-4">
          <input
            v-model="search"
            type="search"
            aria-label="Buscar provincia"
            placeholder="Buscar provincia"
            class="w-full sm:max-w-md px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
          />
        </div>
        <div v-if="!provincias.length" class="card-fbochas p-8 text-center">
          <p class="text-gray-400">Aún no hay provincias cargadas.</p>
        </div>
        <div v-else-if="!filteredProvincias.length" class="card-fbochas p-8 text-center">
          <p class="text-gray-400">No se encontraron provincias.</p>
        </div>
        <ul v-else class="card-fbochas divide-y divide-white/10">
          <li v-for="provincia in filteredProvincias" :key="provincia.id" class="flex items-center justify-between gap-4 p-4">
            <span class="text-white font-medium break-words min-w-0">{{ provincia.name }}</span>
            <button
              type="button"
              class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg border border-sky-400/25 bg-sky-500/10 text-sky-200 transition-colors hover:bg-sky-500/20"
              :aria-label="`Editar ${provincia.name}`"
              :disabled="saving"
              @click="openForm(provincia)"
            >
              <IconFbochas name="edit" :size="16" />
            </button>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { obtenerProvinciasAdmin, guardarProvinciasAdmin } from "@/services/admin/adminModulesService";

const provincias = ref([]);
const loading = ref(true);
const error = ref("");
const search = ref("");
const formOpen = ref(false);
const editingId = ref(null);
const name = ref("");
const nameInput = ref(null);
const saving = ref(false);
const formError = ref("");
const success = ref("");

function normalize(value) {
  return value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const filteredProvincias = computed(() => provincias.value
  .filter((provincia) => normalize(provincia.name).includes(normalize(search.value)))
  .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }))
);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    provincias.value = await obtenerProvinciasAdmin();
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "No se pudieron cargar las provincias.";
  } finally {
    loading.value = false;
  }
}

async function openForm(provincia = null) {
  editingId.value = provincia?.id || null;
  name.value = provincia?.name || "";
  formError.value = "";
  success.value = "";
  formOpen.value = true;
  await nextTick();
  nameInput.value?.focus();
}

async function onSave() {
  if (saving.value) return;
  const provinceName = name.value.trim().replace(/\s+/g, " ");
  formError.value = "";
  if (!provinceName) {
    formError.value = "Ingresá el nombre de la provincia.";
    return;
  }
  saving.value = true;
  try {
    const current = await obtenerProvinciasAdmin();
    if (current.some((item) => item.id !== editingId.value && normalize(item.name) === normalize(provinceName))) {
      formError.value = "Esta provincia ya está cargada.";
      return;
    }
    if (editingId.value && !current.some((item) => item.id === editingId.value)) {
      formError.value = "La provincia ya no está disponible. Volvé a cargar el listado.";
      return;
    }
    const updated = editingId.value
      ? current.map((item) => item.id === editingId.value ? { ...item, name: provinceName } : item)
      : [...current, { id: window.crypto.randomUUID(), name: provinceName }];
    await guardarProvinciasAdmin(updated);
    provincias.value = updated;
    formOpen.value = false;
    search.value = "";
    success.value = "Provincia guardada.";
  } catch (err) {
    formError.value = err.response?.data?.message || err.message || "No se pudo guardar la provincia.";
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
